/**
 * DATA LAYER (DL): QUICK REACTION (REACCIÓN RÁPIDA 1 VS 1)
 * Módulo de comunicación REST nativa (Fetch API) con el backend de Interaktia-V2 para la dinámica Reacción Rápida.
 * Gestiona el CRUD de temas visuales y sprites, configuración de sala, histórico de duelos,
 * carga optimizada de recursos multimedia, comandos de control autoritativo de pantalla gigante
 * y telemetría de interacción de los aficionados móviles (J1 vs J2).
 * 
 * @module utilities/DL/QuickReaction
 */

import SignalRService from '../WS/SignalRService';

/**
 * Resuelve y retorna la URL base para los endpoints del controlador de QuickReaction.
 * Permite compatibilidad con rutas estándar '/api/v1/QuickReaction' o '/api/QuickReaction'.
 * 
 * @returns {string} URL base formateada para el controlador QuickReaction.
 */
export const getApiBaseUrl = () => {
    const rawApiUrl = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const normalizedUrl = rawApiUrl.endsWith('/') ? rawApiUrl.slice(0, -1) : rawApiUrl;
    return `${normalizedUrl}/QuickReaction`;
};

/**
 * Sanea y valida identificadores de sala o parámetros de cabecera HTTP para prevenir inyecciones CRLF.
 * 
 * @param {string|null} val - Valor a sanear.
 * @returns {string} Valor saneado sin saltos de línea ni caracteres de control.
 */
const sanitizeHeaderValue = (val) => {
    if (!val || typeof val !== 'string') return '';
    return val.replace(/[\r\n\t\0]/g, '').trim().substring(0, 100);
};

/**
 * Genera o recupera un identificador único persistente para el dispositivo del aficionado móvil.
 * Busca en localStorage, luego en sessionStorage y en última instancia genera un identificador aleatorio.
 * 
 * @returns {string} ID único de cliente aficionado.
 */
export const getFanClientId = () => {
    if (typeof localStorage !== 'undefined') {
        let clientId = localStorage.getItem('quick_reaction_fan_client_id');
        if (!clientId || !/^[a-zA-Z0-9_\-:]+$/.test(clientId)) {
            clientId = 'fan_qr_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
            localStorage.setItem('quick_reaction_fan_client_id', clientId);
        }
        return clientId;
    }
    if (typeof sessionStorage !== 'undefined') {
        let clientId = sessionStorage.getItem('quick_reaction_fan_client_id');
        if (!clientId || !/^[a-zA-Z0-9_\-:]+$/.test(clientId)) {
            clientId = 'fan_qr_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
            sessionStorage.setItem('quick_reaction_fan_client_id', clientId);
        }
        return clientId;
    }
    return 'fan_qr_' + Math.random().toString(36).substring(2, 15);
};

/**
 * Construye las cabeceras HTTP estándar para las peticiones JSON, adjuntando X-Room-Id si está presente.
 * Sanea estrictamente la cabecera para prevenir inyecciones CRLF y Response Splitting.
 * 
 * @param {string|null} [roomId=null] - Identificador único de la sala.
 * @param {Object} [customHeaders={}] - Cabeceras personalizadas adicionales.
 * @returns {Object} Objeto de cabeceras listo para Fetch API.
 */
const buildHeaders = (roomId = null, customHeaders = {}) => {
    const headers = {
        'Content-Type': 'application/json',
        ...customHeaders
    };

    if (roomId) {
        const cleanRoomId = sanitizeHeaderValue(String(roomId));
        if (cleanRoomId) {
            headers['X-Room-Id'] = cleanRoomId;
        }
    }

    return headers;
};

// =========================================================================
// CRUD DE TEMAS VISUALES Y SPRITES
// =========================================================================

/**
 * Obtiene la lista de temas visuales de Reacción Rápida disponibles con soporte de filtros multi-tenant.
 * 
 * @param {string|null} [roomId=null] - Identificador único de la sala (opcional).
 * @param {string|null} [operatorId=null] - Identificador único del operador (opcional).
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Respuesta con la colección de temas.
 */
export const GetThemesDL = async (roomId = null, operatorId = null) => {
    try {
        const queryParameters = new URLSearchParams();
        if (roomId) {
            queryParameters.append('roomId', String(roomId));
        }
        if (operatorId) {
            queryParameters.append('operatorId', String(operatorId));
        }

        const queryString = queryParameters.toString();
        const requestUrl = `${getApiBaseUrl()}/themes${queryString ? `?${queryString}` : ''}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al consultar los temas de Reacción Rápida.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red o conexión al consultar los temas de Reacción Rápida.'
        };
    }
};

/**
 * Obtiene el detalle de un tema visual por su identificador único GUID.
 * 
 * @param {string} themeIdentifier - Identificador único GUID del tema.
 * @param {string|null} [roomId=null] - Identificador opcional de la sala para la cabecera X-Room-Id.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Detalle del tema encontrado.
 */
export const GetThemeByIdDL = async (themeIdentifier, roomId = null) => {
    try {
        const sanitizedId = encodeURIComponent(String(themeIdentifier));
        const requestUrl = `${getApiBaseUrl()}/theme/${sanitizedId}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al obtener el tema ${themeIdentifier}.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: `Error de red al consultar el tema con ID ${themeIdentifier}.`
        };
    }
};

/**
 * Crea un nuevo tema visual y conjunto de sprites para Reacción Rápida.
 * 
 * @param {Object} themePayload - Objeto con los datos y URLs de sprites del nuevo tema.
 * @param {string|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema creado y confirmado.
 */
export const CreateThemeDL = async (themePayload, roomId = null) => {
    try {
        const effectiveRoomId = roomId || themePayload?.roomId || null;
        const requestUrl = `${getApiBaseUrl()}/theme`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(effectiveRoomId),
            body: JSON.stringify(themePayload)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al crear el tema.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema creado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al crear el tema de Reacción Rápida.'
        };
    }
};

/**
 * Actualiza la configuración y sprites de un tema visual existente de Reacción Rápida.
 * 
 * @param {string} themeIdentifier - Identificador GUID del tema a actualizar.
 * @param {Object} themePayload - Datos modificados del tema.
 * @param {string|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado de la actualización.
 */
export const UpdateThemeDL = async (themeIdentifier, themePayload, roomId = null) => {
    try {
        const effectiveRoomId = roomId || themePayload?.roomId || null;
        const sanitizedId = encodeURIComponent(String(themeIdentifier));
        const requestUrl = `${getApiBaseUrl()}/theme/${sanitizedId}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'PUT',
            headers: buildHeaders(effectiveRoomId),
            body: JSON.stringify(themePayload)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al actualizar el tema.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema actualizado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al actualizar el tema de Reacción Rápida.'
        };
    }
};

/**
 * Elimina un tema personalizado de Reacción Rápida (bloquea presets de sistema).
 * 
 * @param {string} themeIdentifier - Identificador GUID del tema a eliminar.
 * @param {string|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, message?: string}>} Confirmación de eliminación.
 */
export const DeleteThemeDL = async (themeIdentifier, roomId = null) => {
    try {
        const sanitizedId = encodeURIComponent(String(themeIdentifier));
        const requestUrl = `${getApiBaseUrl()}/theme/${sanitizedId}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'DELETE',
            headers: buildHeaders(roomId)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al eliminar el tema.`
            };
        }

        const responseData = await httpResponse.json().catch(() => ({}));
        return {
            success: true,
            message: responseData.message || 'Tema eliminado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al eliminar el tema de Reacción Rápida.'
        };
    }
};

/**
 * Clona un tema existente generando una nueva copia editable e independiente.
 * 
 * @param {string} themeIdentifier - Identificador GUID del tema original a clonar.
 * @param {string|null} [newThemeName=null] - Nombre opcional para la copia clonada.
 * @param {string|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Tema clonado.
 */
export const CloneThemeDL = async (themeIdentifier, newThemeName = null, roomId = null) => {
    try {
        const sanitizedId = encodeURIComponent(String(themeIdentifier));
        const requestUrl = `${getApiBaseUrl()}/theme/${sanitizedId}/clone`;
        const requestBody = newThemeName ? { newName: newThemeName } : {};

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify(requestBody)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al clonar el tema.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Tema clonado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al clonar el tema de Reacción Rápida.'
        };
    }
};

// =========================================================================
// CONFIGURACIÓN E HISTORIAL DE PARTIDAS
// =========================================================================

/**
 * Obtiene la configuración de Reacción Rápida para una sala (auto-inicializa si no existe).
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración activa.
 */
export const GetConfigDL = async (roomId) => {
    try {
        const sanitizedRoomId = encodeURIComponent(String(roomId));
        const requestUrl = `${getApiBaseUrl()}/config/${sanitizedRoomId}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al obtener la configuración.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al obtener la configuración de Reacción Rápida.'
        };
    }
};

/**
 * Guarda o actualiza la configuración de juego de Reacción Rápida para una sala.
 * 
 * @param {Object} configPayload - Datos de configuración a persistir.
 * @param {string|null} [roomId=null] - Identificador opcional de la sala para la cabecera.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Configuración persistida.
 */
export const SaveConfigDL = async (configPayload, roomId = null) => {
    try {
        const effectiveRoomId = roomId || configPayload?.roomId || null;
        const requestUrl = `${getApiBaseUrl()}/config`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(effectiveRoomId),
            body: JSON.stringify(configPayload)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al guardar la configuración.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Configuración guardada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al guardar la configuración de Reacción Rápida.'
        };
    }
};

/**
 * Obtiene el historial de partidas de Reacción Rápida disputadas en una sala.
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {number} [limitQuantity=20] - Cantidad máxima de registros históricos a recuperar.
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} Colección de partidas históricas.
 */
export const GetMatchHistoryDL = async (roomId, limitQuantity = 20) => {
    try {
        const sanitizedRoomId = encodeURIComponent(String(roomId));
        const sanitizedLimit = encodeURIComponent(String(limitQuantity));
        const requestUrl = `${getApiBaseUrl()}/history/${sanitizedRoomId}?limit=${sanitizedLimit}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al obtener el historial de partidas.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al consultar el historial de partidas de Reacción Rápida.'
        };
    }
};

/**
 * Registra y persiste el resultado y tiempos de una partida de Reacción Rápida en base de datos.
 * 
 * @param {Object} matchResultPayload - Métricas y resultado final del duelo.
 * @param {string|null} [roomId=null] - Identificador opcional de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado persistido.
 */
export const SaveMatchResultDL = async (matchResultPayload, roomId = null) => {
    try {
        const effectiveRoomId = roomId || matchResultPayload?.roomId || null;
        const requestUrl = `${getApiBaseUrl()}/history`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(effectiveRoomId),
            body: JSON.stringify(matchResultPayload)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al guardar el resultado de la partida.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Resultado de la partida guardado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al guardar el resultado de la partida de Reacción Rápida.'
        };
    }
};

// =========================================================================
// CARGA DE RECURSOS MULTIMEDIA
// =========================================================================

/**
 * Procesa, optimiza y almacena un recurso gráfico para Reacción Rápida en formato multipart/form-data.
 * No establece la cabecera Content-Type manualmente para permitir que el navegador agregue el límite (boundary).
 * 
 * @param {File|Blob|FormData} fileOrFormData - Archivo o instancia de FormData a transmitir.
 * @param {string|null} [roomId=null] - Identificador opcional de la sala para la cabecera X-Room-Id.
 * @returns {Promise<{success: boolean, data?: Object, url?: string, message?: string}>} URL y metadata del archivo.
 */
export const UploadAssetDL = async (fileOrFormData, roomId = null) => {
    try {
        let requestFormData = fileOrFormData;
        if (!(fileOrFormData instanceof FormData)) {
            requestFormData = new FormData();
            requestFormData.append('file', fileOrFormData);
        }

        const headers = {};
        if (roomId) {
            const cleanRoomId = sanitizeHeaderValue(String(roomId));
            if (cleanRoomId) {
                headers['X-Room-Id'] = cleanRoomId;
            }
        }

        const requestUrl = `${getApiBaseUrl()}/upload`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: headers,
            body: requestFormData
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al subir el archivo multimedia.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: Boolean(responseData.success !== false),
            data: responseData,
            url: responseData.url || (responseData.data && responseData.data.url),
            message: responseData.message || 'Archivo subido y procesado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al subir el archivo multimedia a Reacción Rápida.'
        };
    }
};

/**
 * Procesa y almacena un lote de hasta 50 imágenes multimedia para sprites o fondos de Reacción Rápida.
 * 
 * @param {Array<File>|FileList|FormData} filesOrFormData - Colección de archivos o FormData para envío masivo.
 * @param {string|null} [roomId=null] - Identificador opcional de la sala para la cabecera X-Room-Id.
 * @returns {Promise<{success: boolean, data?: Object, urls?: Array<string>, files?: Array<Object>, message?: string}>} Reporte de procesamiento por lote.
 */
export const UploadBatchMediaDL = async (filesOrFormData, roomId = null) => {
    try {
        let requestFormData = filesOrFormData;
        if (!(filesOrFormData instanceof FormData)) {
            requestFormData = new FormData();
            const fileListArray = Array.from(filesOrFormData || []);
            fileListArray.forEach((fileItem) => {
                requestFormData.append('files', fileItem);
            });
        }

        const headers = {};
        if (roomId) {
            const cleanRoomId = sanitizeHeaderValue(String(roomId));
            if (cleanRoomId) {
                headers['X-Room-Id'] = cleanRoomId;
            }
        }

        const requestUrl = `${getApiBaseUrl()}/upload-batch`;
        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: headers,
            body: requestFormData
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al procesar el lote de archivos.`
            };
        }

        const responseData = await httpResponse.json();
        const filesList = responseData.files || responseData.data?.files || [];
        const extractedUrls = (Array.isArray(responseData.urls) && responseData.urls.length > 0)
            ? responseData.urls
            : (Array.isArray(filesList) ? filesList.filter((f) => f.success && f.url).map((f) => f.url) : []);

        return {
            success: Boolean(responseData.success !== false && (extractedUrls.length > 0 || responseData.totalFiles === 0)),
            data: responseData,
            urls: extractedUrls,
            files: filesList,
            count: extractedUrls.length,
            message: responseData.message || 'Lote de archivos procesado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al subir el lote multimedia de Reacción Rápida.'
        };
    }
};

// =========================================================================
// COMANDOS DE CONTROL DE SALA EN TIEMPO REAL
// =========================================================================

/**
 * Proyecta la portada de Reacción Rápida en la pantalla gigante y sincroniza a los aficionados.
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string|null} [themeIdentifier=null] - Identificador GUID del tema visual a proyectar (opcional).
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado actualizado de la partida.
 */
export const ShowCoverDL = async (roomId, themeIdentifier = null) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/cover`;
        const requestBody = {
            roomId: roomId,
            action: 'show_cover'
        };

        if (themeIdentifier) {
            requestBody.themeId = themeIdentifier;
        }

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify(requestBody)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al proyectar la portada.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Portada de Reacción Rápida proyectada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al proyectar la portada de Reacción Rápida.'
        };
    }
};

/**
 * Proyecta la escena de combate (1 vs 1) y prepara los slots y sprites de los jugadores.
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string|null} [themeIdentifier=null] - Identificador GUID del tema visual activo (opcional).
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado en vivo de la escena.
 */
export const ShowGameSceneDL = async (roomId, themeIdentifier = null) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/game-scene`;
        const requestBody = {
            roomId: roomId,
            action: 'show_game'
        };

        if (themeIdentifier) {
            requestBody.themeId = themeIdentifier;
        }

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify(requestBody)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al preparar la escena de juego.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Escena de Reacción Rápida proyectada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al preparar la escena de juego de Reacción Rápida.'
        };
    }
};

/**
 * Inicia la secuencia oficial de partida (conteo 3, 2, 1 -> retardo aleatorio -> señal de disparo).
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado de la secuencia iniciada.
 */
export const StartMatchDL = async (roomId) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/start`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify({ roomId: roomId, action: 'start' })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al iniciar la partida.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Secuencia de partida iniciada exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al iniciar la partida de Reacción Rápida.'
        };
    }
};

/**
 * Reinicia el duelo a 'game_ready' para una revancha inmediata, conservando jugadores y tema activo.
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado reiniciado.
 */
export const ResetMatchDL = async (roomId) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/reset`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify({ roomId: roomId, action: 'reset' })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al reiniciar la partida.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Partida reiniciada exitosamente para revancha.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al reiniciar la partida de Reacción Rápida.'
        };
    }
};

/**
 * Fuerza la finalización anticipada del duelo en curso.
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Resultado final calculado.
 */
export const FinishEarlyDL = async (roomId) => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/finish-early`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify({ roomId: roomId, action: 'finish_early' })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al finalizar la partida.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Partida finalizada anticipadamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al finalizar anticipadamente la partida de Reacción Rápida.'
        };
    }
};

/**
 * Consulta el estado actual en memoria RAM de la partida para una sala específica.
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado autoritativo en vivo.
 */
export const GetMatchStateDL = async (roomId) => {
    try {
        const sanitizedRoomId = encodeURIComponent(String(roomId));
        const requestUrl = `${getApiBaseUrl()}/state/${sanitizedRoomId}`;

        const httpResponse = await fetch(requestUrl, {
            method: 'GET',
            headers: buildHeaders(roomId)
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al consultar el estado de la partida.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al consultar el estado de la partida de Reacción Rápida.'
        };
    }
};

// =========================================================================
// INTERACCIÓN MÓVIL / AFICIONADO (FANS)
// =========================================================================

/**
 * Asigna a un participante a un slot ("player1" o "player2") aplicando el patrón Tacos (kicked_out al ocupante previo).
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string} side - Bando seleccionado ("player1" | "player2" | "J1" | "J2").
 * @param {string} [playerName=''] - Nombre o alias del jugador participante.
 * @param {string} [connectionId=''] - Identificador SignalR de la conexión activa.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Confirmación y estado actualizado.
 */
export const SelectPlayerSideDL = async (roomId, side, playerName = '', connectionId = '') => {
    try {
        const activeConnectionId = connectionId || SignalRService.getConnectionId() || getFanClientId();
        const requestUrl = `${getApiBaseUrl()}/fan/select-side`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify({
                roomId: roomId,
                side: side,
                playerName: playerName,
                connectionId: activeConnectionId
            })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al seleccionar personaje.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Lado asignado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al seleccionar personaje en Reacción Rápida.'
        };
    }
};

/**
 * Registra la pulsación/disparo táctil de un participante móvil.
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string} [connectionId=''] - Identificador de conexión SignalR del participante.
 * @param {number} [clientCalculatedTimeMs=0] - Tiempo de reacción calculado en el cliente en ms (modo ClientOffsetSync).
 * @param {string} [side=''] - Bando o lado del participante ("player1" | "player2").
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado actualizado tras la pulsación.
 */
export const SendTapDL = async (roomId, connectionId = '', clientCalculatedTimeMs = 0, side = '') => {
    try {
        const activeConnectionId = connectionId || SignalRService.getConnectionId() || getFanClientId();
        const requestUrl = `${getApiBaseUrl()}/fan/tap`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify({
                roomId: roomId,
                connectionId: activeConnectionId,
                clientCalculatedTimeMs: Number(clientCalculatedTimeMs) || 0,
                side: side || undefined
            })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al registrar la pulsación.`
            };
        }

        const responseData = await httpResponse.json().catch(() => ({ success: true }));
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al registrar la pulsación en Reacción Rápida.'
        };
    }
};

/**
 * Actualiza en tiempo real los nombres de los jugadores y/o el modo de pantalla desde el panel de operador.
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string} [player1Name=''] - Nombre para el Jugador 1.
 * @param {string} [player2Name=''] - Nombre para el Jugador 2.
 * @param {string} [displayMode='single_target'] - Modo de pantalla ("single_target" | "dual_duel").
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} Estado actualizado.
 */
export const UpdatePlayersAndModeDL = async (roomId, player1Name = '', player2Name = '', displayMode = 'single_target') => {
    try {
        const requestUrl = `${getApiBaseUrl()}/control/update-players`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify({
                roomId: roomId,
                player1Name: player1Name,
                player2Name: player2Name,
                displayMode: displayMode
            })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al actualizar jugadores y modo.`
            };
        }

        const responseData = await httpResponse.json();
        return {
            success: true,
            data: responseData.data !== undefined ? responseData.data : responseData,
            message: responseData.message || 'Jugadores y modo actualizados exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al actualizar jugadores y modo de Reacción Rápida.'
        };
    }
};

/**
 * Desvincula a un jugador al salir o desconectarse de la sala.
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @param {string} [connectionId=''] - Identificador SignalR de la conexión del participante.
 * @returns {Promise<{success: boolean, message?: string}>} Confirmación de desvinculación.
 */
export const UnregisterFanDL = async (roomId, connectionId = '') => {
    try {
        const activeConnectionId = connectionId || SignalRService.getConnectionId() || getFanClientId();
        const requestUrl = `${getApiBaseUrl()}/fan/unregister`;

        const httpResponse = await fetch(requestUrl, {
            method: 'POST',
            headers: buildHeaders(roomId),
            body: JSON.stringify({
                roomId: roomId,
                connectionId: activeConnectionId
            })
        });

        if (!httpResponse.ok) {
            const errorPayload = await httpResponse.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || `Error HTTP ${httpResponse.status} al desvincular jugador.`
            };
        }

        const responseData = await httpResponse.json().catch(() => ({}));
        return {
            success: true,
            message: responseData.message || 'Jugador desvinculado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: 'Error de red al desvincular jugador en Reacción Rápida.'
        };
    }
};

// Aliases semánticos para interoperabilidad fluida
export const GetThemesQuickReaction = GetThemesDL;
export const GetThemeByIdQuickReaction = GetThemeByIdDL;
export const CreateThemeQuickReaction = CreateThemeDL;
export const UpdateThemeQuickReaction = UpdateThemeDL;
export const DeleteThemeQuickReaction = DeleteThemeDL;
export const CloneThemeQuickReaction = CloneThemeDL;
export const GetConfigQuickReaction = GetConfigDL;
export const SaveConfigQuickReaction = SaveConfigDL;
export const GetMatchHistoryQuickReaction = GetMatchHistoryDL;
export const GetMatchesHistoryQuickReaction = GetMatchHistoryDL;
export const SaveMatchResultQuickReaction = SaveMatchResultDL;
export const UploadMediaQuickReaction = UploadAssetDL;
export const UploadAssetQuickReaction = UploadAssetDL;
export const UploadBatchMediaQuickReaction = UploadBatchMediaDL;
export const ShowCoverQuickReaction = ShowCoverDL;
export const ShowGameQuickReaction = ShowGameSceneDL;
export const ShowGameSceneQuickReaction = ShowGameSceneDL;
export const StartMatchSequenceQuickReaction = StartMatchDL;
export const StartMatchQuickReaction = StartMatchDL;
export const ResetMatchQuickReaction = ResetMatchDL;
export const FinishEarlyQuickReaction = FinishEarlyDL;
export const GetMatchStateQuickReaction = GetMatchStateDL;

export default {
    getApiBaseUrl,
    getFanClientId,
    GetThemesDL,
    GetThemeByIdDL,
    CreateThemeDL,
    UpdateThemeDL,
    DeleteThemeDL,
    CloneThemeDL,
    GetConfigDL,
    SaveConfigDL,
    GetMatchHistoryDL,
    SaveMatchResultDL,
    UploadAssetDL,
    UploadBatchMediaDL,
    ShowCoverDL,
    ShowGameSceneDL,
    StartMatchDL,
    ResetMatchDL,
    FinishEarlyDL,
    GetMatchStateDL,
    SelectPlayerSideDL,
    SendTapDL,
    UnregisterFanDL,
    // Aliases
    GetThemesQuickReaction,
    CreateThemeQuickReaction,
    UpdateThemeQuickReaction,
    DeleteThemeQuickReaction,
    CloneThemeQuickReaction,
    GetConfigQuickReaction,
    SaveConfigQuickReaction,
    GetMatchesHistoryQuickReaction,
    UploadMediaQuickReaction,
    UploadBatchMediaQuickReaction,
    ShowCoverQuickReaction,
    ShowGameQuickReaction,
    StartMatchSequenceQuickReaction,
    ResetMatchQuickReaction
};
